import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authApi } from "@/services/auth";

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  avatar_url?: string | null;
  bio?: string;
  location?: {
    longitude: number;
    latitude: number;
  } | null;
  post_count?: number;
  communities?: unknown[];
  joined_at?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Actions
  handleOAuthCallback: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  clearError: () => void;
  getCurrentUser: () => Promise<User>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      handleOAuthCallback: async (code: string) => {
        try {
          set({ isLoading: true, error: null });
          const response = await authApi.googleAuth(code);
          const { access_token, refresh_token, user } = response;

          localStorage.setItem("auth_token", access_token);
          localStorage.setItem("refresh_token", refresh_token);

          set({
            user: {
              ...user,
              display_name: `${user.first_name} ${user.last_name}`,
            },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: unknown) {
          console.error("=== Auth Error ===");
          console.error(error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to complete authentication";
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          // Clear tokens and state
          localStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");
          set({
            user: null,
            isAuthenticated: false,
            error: null,
          });
          console.log("=== Logged Out ===");
        } catch (error) {
          console.error("Logout error:", error);
        }
      },

      updateProfile: async (data: Partial<User>) => {
        try {
          set({ isLoading: true, error: null });

          // TODO: Đợi API từ backend
          // Tạm thời update local state
          set((state) => ({
            user: state.user ? { ...state.user, ...data } : null,
            isLoading: false,
          }));
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to update profile";
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      getCurrentUser: async () => {
        try {
          // TODO: Đợi API từ backend
          // Tạm thời return mock user
          const mockUser = {
            id: "mock-user-id",
            email: "user@example.com",
            first_name: "Test",
            last_name: "User",
            display_name: "Test User",
            avatar_url: null,
            bio: "",
            location: null,
            post_count: 0,
            communities: [],
            joined_at: new Date().toISOString(),
          };

          set({ user: mockUser, isAuthenticated: true });
          return mockUser;
        } catch (error: unknown) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to get current user";
          set({
            error: errorMessage,
            isAuthenticated: false,
            user: null,
          });
          throw error;
        }
      },
    }),
    {
      name: "auth-storage",
    },
  ),
);
